/**
 * @description Presentation maps for the conversation "semáforo" (traffic light).
 * The backend calculates and exposes a normalized `status` object per conversation
 * ({ value, isResolved, requiresReview, trafficLight, analysisState, ... }). The frontend only
 * consumes and represents; it does not recalculate anything.
 * Labels are resolved through i18n (react-i18next, namespace 'chats').
 */

// status.trafficLight -> color
export const TRAFFIC_LIGHT_COLOR = {
  green: 'var(--secondary-300)',
  yellow: '#FACC15',
  red: '#EF4444',
  gray: '#9CA3AF'
}

export const DEFAULT_TRAFFIC_LIGHT = 'gray'
export const DEFAULT_ATTENTION_STATUS = 'not_required'

const VALID_ATTENTION_STATUS = ['attended', 'not_attended', 'not_required', 'resolved']

/**
 * @description Returns a safe traffic light color key, falling back to gray when missing/unknown.
 * @param {string} status raw conv.status.trafficLight
 * @return {string}
 */
export const getTrafficLightStatus = (status) =>
  TRAFFIC_LIGHT_COLOR[status] ? status : DEFAULT_TRAFFIC_LIGHT

/**
 * @description Returns a safe attention value, falling back to not_required when missing/unknown.
 * @param {string} attentionValue raw conv.status.value
 * @return {string}
 */
export const getAttentionStatus = (attentionValue) =>
  VALID_ATTENTION_STATUS.includes(attentionValue) ? attentionValue : DEFAULT_ATTENTION_STATUS

/**
 * @description Builds the tooltip / aria-label text: "{semáforo} · {atención}" using i18n.
 * `value` now encodes 'resolved' directly, so no special-casing is needed.
 * @param {Function} t react-i18next translator bound to the 'chats' namespace
 * @param {string} trafficLight raw conv.status.trafficLight
 * @param {string} attentionValue raw conv.status.value
 * @return {string}
 */
export const getTrafficLightTooltip = (t, trafficLight, attentionValue) => {
  const safeStatus = getTrafficLightStatus(trafficLight)
  const secondary = t(`attention.${getAttentionStatus(attentionValue)}`)
  return `${t(`trafficLight.${safeStatus}`)} · ${secondary}`
}
